// ReviewPack.js — shared renderer for the automated 12-month evidence pack
// (narrative, insights, near-miss + pattern analysis, PMIs, attendance,
// engagement, SOPs, year-on-year). Used by BOTH the staff review popup
// (Staff.js) and the appraisal dialogue (StaffAppraisal.js) so the two can
// never drift apart. Accepts a live review doc or a completed appraisal's
// reviewSnapshot — same shape either way.

import React from "react";
import { maskTextWithNames } from "./nameMask";

function fmtDate(d) {
  if (!d) return "—";
  const t = new Date(d);
  if (isNaN(t.getTime())) return "—";
  return t.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtNum(n, dp = 2) {
  if (n === null || n === undefined || isNaN(Number(n))) return "—";
  const v = Number(n);
  return Number.isInteger(v) ? String(v) : v.toFixed(dp);
}

function fmtPct(decimal) {
  if (decimal === null || decimal === undefined || isNaN(Number(decimal))) return "—";
  return `${Math.round(Number(decimal) * 100)}%`;
}

const POLARITY_STYLE = {
  positive: { bg: "#F6FFED", border: "#B7EB8F", text: "#389E0D", label: "Commendation" },
  concern:  { bg: "#FFF1F0", border: "#FFA39E", text: "#A8071A", label: "Discussion point" },
  neutral:  { bg: "#F5F5F5", border: "#D9D9D9", text: "#434343", label: "Note" }
};

function MetricRow({ label, block, valueSuffix = "" }) {
  if (!block) return null;
  return (
    <tr>
      <td style={{ padding: "6px 10px", fontSize: 13, borderBottom: "1px solid #f0f1f3" }}>{label}</td>
      <td style={{ padding: "6px 10px", fontSize: 13, borderBottom: "1px solid #f0f1f3", textAlign: "right", fontWeight: 600 }}>
        {fmtNum(block.value)}{valueSuffix}
      </td>
      <td style={{ padding: "6px 10px", fontSize: 13, borderBottom: "1px solid #f0f1f3", textAlign: "right" }}>{fmtNum(block.perShift)}</td>
      <td style={{ padding: "6px 10px", fontSize: 13, borderBottom: "1px solid #f0f1f3", textAlign: "right" }}>{fmtNum(block.cohortAvg)}</td>
      <td style={{
        padding: "6px 10px", fontSize: 13, borderBottom: "1px solid #f0f1f3", textAlign: "right",
        color: block.ratio !== null && block.ratio >= 1.5 ? "#CF1322" : (block.ratio !== null && block.ratio <= 0.5 ? "#5D5D5D" : "#262626")
      }}>
        {block.ratio !== null && block.ratio !== undefined ? `${fmtNum(block.ratio)}×` : "—"}
      </td>
    </tr>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
      color: "#5D5D5D", margin: "18px 0 8px"
    }}>{children}</div>
  );
}

/* ---------------- Full review popup ---------------- */


const thSmall = {
  padding: "6px 10px", fontSize: 11, fontWeight: 700, textTransform: "uppercase",
  letterSpacing: 0.4, color: "#5D5D5D", borderBottom: "2px solid #F0F0F0", textAlign: "right"
};

const td = { padding: "6px 10px", fontSize: 13, borderBottom: "1px solid #f0f1f3" };

export default function ReviewPackView({ review, staffName, masked }) {
  if (!review) return null;
  const narrative = maskTextWithNames(review.narrative, [review.staffName || staffName], masked);
  return (
    <>
              {/* Narrative */}
              <SectionTitle>Narrative summary</SectionTitle>
              <div style={{
                fontSize: 14, lineHeight: 1.65, color: "#262626", whiteSpace: "pre-wrap",
                background: "#FAFAFA", border: "1px solid #eef0f3", borderRadius: 6, padding: "12px 14px"
              }}>
                {narrative || "—"}
              </div>

              {/* Insights */}
              <SectionTitle>
                Insights ({(review.insights || []).length})
              </SectionTitle>
              {(review.insights || []).length === 0 ? (
                <div style={{ fontSize: 13, color: "#5D5D5D" }}>
                  No statistical outliers identified this year.
                </div>
              ) : (
                review.insights.map((ins, i) => {
                  const st = POLARITY_STYLE[ins.polarity] || POLARITY_STYLE.neutral;
                  return (
                    <div key={i} style={{
                      background: st.bg, border: `1px solid ${st.border}`, borderRadius: 6,
                      padding: "10px 12px", marginBottom: 8
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
                        <span style={{ fontWeight: 700, fontSize: 13, color: st.text }}>
                          {ins.id} · {ins.title}
                        </span>
                        <span style={{ fontSize: 11, color: st.text, whiteSpace: "nowrap" }}>
                          {st.label} · severity {ins.severity}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, lineHeight: 1.55, color: "#262626", marginTop: 4 }}>
                        {maskTextWithNames(ins.detail, [review.staffName], masked)}
                      </div>
                    </div>
                  );
                })
              )}

              {/* Near misses */}
              <SectionTitle>
                Near misses — cohort: {review.cohort?.type === "role"
                  ? `${review.cohort.roleName || "role-matched"} (${review.cohort.size})`
                  : `whole pharmacy (${review.cohort?.size ?? "—"})`}
              </SectionTitle>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...thSmall, textAlign: "left" }}>Metric</th>
                    <th style={thSmall}>Count</th>
                    <th style={thSmall}>Per shift</th>
                    <th style={thSmall}>Cohort avg</th>
                    <th style={thSmall}>Ratio</th>
                  </tr>
                </thead>
                <tbody>
                  <MetricRow label="As dispenser (errors attributed)" block={review.nearMisses?.asDispenser} />
                  <MetricRow label="As checker (errors caught)" block={review.nearMisses?.asChecker} />
                  {review.nearMisses?.asReporter && (
                    <MetricRow label="As reporter" block={review.nearMisses.asReporter} />
                  )}
                </tbody>
              </table>
              {(review.nearMisses?.asDispenserCategories || []).length > 0 && (
                <div style={{ fontSize: 13, color: "#434343", marginTop: 8 }}>
                  Error types: {review.nearMisses.asDispenserCategories
                    .map(c => `${c.label} (${c.count})`).join(", ")}
                </div>
              )}

              {/* Deep pattern analysis (shared rule set with the branch monthly engine) */}
              {review.nearMissAnalysis && !review.nearMissAnalysis.error && review.nearMissAnalysis.total > 0 && (
                <div style={{
                  marginTop: 10, border: "1px solid #F0F5FF", background: "#eef2ff",
                  borderRadius: 6, padding: "10px 12px"
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "#4338ca", marginBottom: 6 }}>
                    Pattern analysis — {review.nearMissAnalysis.total} incident{review.nearMissAnalysis.total === 1 ? "" : "s"}
                    {review.nearMissAnalysis.shareOfPharmacy !== null && <> · {review.nearMissAnalysis.shareOfPharmacy}% of branch incidents</>}
                  </div>
                  {/* Monthly distribution mini-bars */}
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 40, marginBottom: 4 }}>
                    {Object.entries(review.nearMissAnalysis.monthly || {}).map(([k, v]) => {
                      const max = Math.max(1, ...Object.values(review.nearMissAnalysis.monthly));
                      return (
                        <div key={k} title={`${k}: ${v}`} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                          <div style={{
                            width: "100%", borderRadius: 2,
                            height: `${Math.max(2, (v / max) * 32)}px`,
                            background: v > 0 ? "#2F54EB" : "#F0F0F0"
                          }} />
                          <span style={{ fontSize: 8, color: "#5D5D5D" }}>{k.slice(5)}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 12, color: "#262626", lineHeight: 1.6 }}>
                    {(review.nearMissAnalysis.dayOfWeek || []).filter(d => d.count > 0).length > 0 && (
                      <div>Days: {review.nearMissAnalysis.dayOfWeek.filter(d => d.count > 0).map(d => `${d.day} ${d.count}`).join(", ")}</div>
                    )}
                    {(review.nearMissAnalysis.causes || []).length > 0 && (
                      <div>Recorded causes: {review.nearMissAnalysis.causes.map(c => `${c.family} (${c.count})`).join(", ")}
                        {review.nearMissAnalysis.causeCoverage !== null && <span style={{ color: "#5D5D5D" }}> · cause recorded on {review.nearMissAnalysis.causeCoverage}% of incidents</span>}
                      </div>
                    )}
                    {review.nearMissAnalysis.checker?.total > 0 && (
                      <div>As checker, strongest at catching: {review.nearMissAnalysis.checker.categories.slice(0, 3).map(c => `${c.label} (${c.count})`).join(", ")}</div>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: "#5D5D5D", marginTop: 6 }}>
                    Same rule set as the branch monthly near-miss engine (near_miss_insight_config) — pattern findings appear as SN-series insights above.
                  </div>
                </div>
              )}

              {/* Medication incidents */}
              <SectionTitle>Patient medication incidents</SectionTitle>
              <div style={{ fontSize: 13, color: "#262626", lineHeight: 1.6 }}>
                Involved in <b>{review.medicationIncidents?.involvedCount ?? 0}</b> incident{(review.medicationIncidents?.involvedCount ?? 0) === 1 ? "" : "s"}
                {" "}({review.medicationIncidents?.asDispenser ?? 0} as dispenser, {review.medicationIncidents?.asChecker ?? 0} as checker)
                {" "}· cohort average {fmtNum(review.medicationIncidents?.cohortAvg)}
                {review.medicationIncidents?.ratio !== null && review.medicationIncidents?.ratio !== undefined && (
                  <> · ratio {fmtNum(review.medicationIncidents.ratio)}×</>
                )}
              </div>

              {/* Attendance */}
              <SectionTitle>Attendance</SectionTitle>
              {review.attendance?.hasData ? (
                <div style={{ fontSize: 13, color: "#262626", lineHeight: 1.7 }}>
                  <b>{review.attendance.shiftsWorked}</b> shifts worked ·{" "}
                  <b>{review.attendance.lateCount}</b> late starts ({fmtPct(review.attendance.lateRate)}
                  {review.attendance.cohortLateRate !== null && <>, cohort {fmtPct(review.attendance.cohortLateRate)}</>}
                  {review.attendance.lateCount > 0 && <>, avg {review.attendance.avgLatenessMins} min</>}) ·{" "}
                  <b>{review.attendance.earlyCount}</b> early departures ({fmtPct(review.attendance.earlyRate)}
                  {review.attendance.cohortEarlyRate !== null && <>, cohort {fmtPct(review.attendance.cohortEarlyRate)}</>}) ·{" "}
                  <b>{review.attendance.narrativeNotes}</b> shift notes
                  <div style={{ fontSize: 11, color: "#8C8C8C", marginTop: 4 }}>
                    Heuristic: {review.attendance.heuristic}
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "#5D5D5D" }}>
                  No attendance records matched this staff member in the window — per-shift comparisons unavailable.
                </div>
              )}

              {/* Activity sources */}
              {(review.activitySources || []).length > 0 && (
                <>
                  <SectionTitle>Engagement (activity sources)</SectionTitle>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={{ ...thSmall, textAlign: "left" }}>Source</th>
                        <th style={thSmall}>Count</th>
                        <th style={thSmall}>Per shift</th>
                        <th style={thSmall}>Cohort avg</th>
                        <th style={thSmall}>Ratio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {review.activitySources.map((src, i) => (
                        <MetricRow key={i} label={src.label} block={src} />
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {/* SOP reading cards */}
              <SectionTitle>SOP reading cards</SectionTitle>
              {!review.sops ? (
                <div style={{ fontSize: 13, color: "#b45309" }}>
                  This review was computed by an engine version without SOP analysis.
                  The backend process is still running the old engine module — restart the
                  backend and reopen this review.
                </div>
              ) : review.sops.totalCards === 0 ? (
                <div style={{ fontSize: 13, color: "#5D5D5D" }}>
                  No SOP reading cards are assigned to this staff member.
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: "#262626", lineHeight: 1.6 }}>
                    <b>{review.sops.read}</b> of <b>{review.sops.totalCards}</b> read and signed
                    {" "}({fmtPct(review.sops.readPct)}
                    {review.sops.cohortReadPct !== null && <>, cohort {fmtPct(review.sops.cohortReadPct)}</>})
                    {" "}· <b style={{ color: review.sops.remindedInWindow > 0 ? "#b45309" : "#389E0D" }}>
                      {review.sops.remindedInWindow}
                    </b> card{review.sops.remindedInWindow === 1 ? "" : "s"} needed a reminder this year
                  </div>
                  {(review.sops.unreadCards || []).length > 0 && (
                    <div style={{
                      marginTop: 8, border: "1px solid #FFE58F", background: "#FFFBE6",
                      borderRadius: 6, padding: "8px 12px"
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#AD4E00", marginBottom: 4 }}>
                        Unread ({review.sops.unread}) — oldest first
                      </div>
                      {review.sops.unreadCards.map((c, i) => (
                        <div key={i} style={{ fontSize: 13, color: "#262626", padding: "3px 0", borderBottom: i < review.sops.unreadCards.length - 1 ? "1px solid #FFF7E6" : "none" }}>
                          {c.title}
                          <span style={{ color: "#8C8C8C", fontSize: 11, marginLeft: 8 }}>
                            assigned {fmtDate(c.assignedAt)}
                            {c.dueDate && <> · due {fmtDate(c.dueDate)}</>}
                            {c.lastReminderSentAt && <> · last reminded {fmtDate(c.lastReminderSentAt)}</>}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: "#8C8C8C", marginTop: 4 }}>
                    {review.sops.note}
                  </div>
                </>
              )}

              {/* Year-on-year */}
              <SectionTitle>Year-on-year</SectionTitle>
              {review.comparison?.hasPriorYear ? (
                <div style={{ fontSize: 13, color: "#262626", lineHeight: 1.6 }}>
                  vs {review.comparison.priorReviewYear}: near misses as dispenser{" "}
                  {review.comparison.priorNearMissesAsDispenser} → {review.nearMisses?.asDispenser?.value}
                  {review.comparison.lateRateDelta !== null && (
                    <> · late-start rate delta {review.comparison.lateRateDelta > 0 ? "+" : ""}{fmtPct(review.comparison.lateRateDelta)}</>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "#5D5D5D" }}>
                  No previous review on record — this is the baseline year.
                </div>
              )}

              <div style={{ fontSize: 11, color: "#8C8C8C", marginTop: 18 }}>
                Computed {fmtDate(review.computedAt)} · engine {review.meta?.engine || "—"} ·
                {" "}discussion aid assembled from operational records, not a performance verdict.
              </div>
    </>
  );
}
